import { useEffect, useState, useCallback } from "react";
import { isObject } from "../utils";
import React from "react";

interface UseFormOptions {
  validations?: Record<string, any>;
}

interface UseFormReturn {
  form: Record<string, any>;
  updateForm: (arg: any, options?: any) => void;
  clearForm: (id: string | string[]) => void;
  setFormInit: (data: Record<string, any>, options?: any) => void;
  errors: Record<string, any>;
  isValid: boolean;
  register: (
    key: string,
    options?: any
  ) => {
    id: string;
    value: any;
    onChange: (event: { value: any }) => void;
    [key: string]: any;
  };
  hasBeenInit: Record<string, any>;
  isFormValid: () => boolean;
}

export const useForm = (options: UseFormOptions): UseFormReturn => {
  // Normalize options without changing the API
  const normalizedOptions = React.useMemo(
    () => ({
      validations: options?.validations || {},
    }),
    [
      // Stringify validations to ensure stable comparison
      JSON.stringify(options?.validations),
    ]
  );

  const [form, setForm] = useState<Record<string, any>>({});
  const [isValid, setIsValid] = useState(false);
  const [errors, setErrors] = useState({});
  const [hasBeenInit, setHasBeenInit] = useState({});

  // Separate validation effect to reduce unnecessary runs
  useEffect(() => {
    // Skip validation if no validations defined
    if (
      !normalizedOptions.validations ||
      Object.keys(normalizedOptions.validations).length === 0
    ) {
      setIsValid(true);
      return;
    }

    const validationResult = isFormValid();
    setIsValid(validationResult);
  }, [
    // Only depend on form values that are actually being validated
    ...Object.keys(normalizedOptions.validations).map((key) => form[key]),
    JSON.stringify(normalizedOptions.validations),
  ]);

  const updateForm = useCallback(
    (arg: { id: string; value: any } | ((prevForm: any) => any)) => {
      if (typeof arg === "function") {
        setForm(arg);
        // Note: Functional updates need to handle hasBeenInit manually if needed
      } else {
        const { id, value } = arg;
        setForm((prevForm) => {
          if (
            !prevForm[id] ||
            (Array.isArray(prevForm[id]) && prevForm[id].length === 0) ||
            (isObject(prevForm[id]) && Object.keys(prevForm[id]).length === 0)
          ) {
            setHasBeenInit((prev) => ({ ...prev, [id]: true }));
          }
          return { ...prevForm, [id]: value };
        });
      }
    },
    [] // Empty dependency array makes this function stable
  );

  const register = useCallback(
    function register(key: string, options: any = {}) {
      return {
        id: key,
        value: form[key],
        onChange: ({ value }: { value: any }) => updateForm({ id: key, value }),
        ...options,
      };
    },
    [form, updateForm] // updateForm is now stable
  );

  const setFormInit = useCallback(
    (
      obj: Record<string, any>,
      options?: { reset?: boolean; valid?: boolean }
    ) => {
      if (options?.reset) {
        setForm({ ...obj });
      } else {
        setForm((prevForm) => ({ ...prevForm, ...obj }));
      }

      // Mark all incoming keys as initialized
      const initKeys = {};
      Object.keys(obj).forEach((key) => {
        initKeys[key] = true;
      });
      setHasBeenInit((prev) => ({ ...prev, ...initKeys }));

      if (options?.valid) {
        setIsValid(true);
      }
    },
    [] // Empty dependency array makes this function stable
  );

  const clearForm = useCallback(
    (id: string | string[]) => {
      if (Array.isArray(id)) {
        setForm((prevForm) => {
          const newForm = { ...prevForm };
          id.forEach((item) => {
            delete newForm[item];
          });
          return newForm;
        });
        return;
      }
      if (id) {
        setForm((prevForm) => {
          const newForm = { ...prevForm };
          delete newForm[id];
          return newForm;
        });
        return;
      }

      return setForm({});
    },
    [] // No dependency on form needed with functional setState
  );

  const valueIsEmpty = useCallback(function valueIsEmpty(value: any) {
    // Handle null/undefined case first
    if (value === null || value === undefined) {
      return true;
    }

    // Handle Date objects
    if (value instanceof Date) {
      return isNaN(value.getTime());
    }

    // Handle plain objects (but not Date or other special objects)
    if (value.constructor === Object && Object.keys(value).length === 0) {
      return true;
    }

    // Handle arrays
    if (Array.isArray(value)) {
      return value.length === 0;
    }

    // Handle strings
    if (typeof value === "string") {
      return value.trim() === "";
    }

    // Handle numbers
    if (typeof value === "number") {
      return isNaN(value);
    }

    // Handle booleans
    if (typeof value === "boolean") {
      return false;
    }

    return false;
  }, []);

  const isFormValid = useCallback(
    function isFormValid() {
      let validations = normalizedOptions.validations;

      if (!validations || Object.keys(validations).length === 0) {
        return true;
      }

      let valid = true;
      let newErrors = {};

      Object.keys(validations).forEach((key) => {
        let hasIdBeenInit = hasBeenInit[key];
        const value = form[key];
        const validation = validations[key];

        // Skip validation if field hasn't been initialized
        if (!hasIdBeenInit) {
          valid = false;
          return;
        }

        // Regex
        const pattern = validation?.pattern;
        if (pattern?.value && !RegExp(pattern.value).test(value)) {
          valid = false;
          newErrors[key] = pattern.message;
        }

        // Custom
        const custom = validation?.custom;
        const customIsValid = custom?.isValid(value, {
          validations: validations[key],
          form,
          isFormValid,
          validateEmail,
          valueIsEmpty,
          hasBeenInit,
        });
        if (custom?.isValid) {
          if (isObject(customIsValid) && !customIsValid.isValid) {
            valid = false;
            newErrors = { ...newErrors, ...customIsValid.errors };
          }
        }

        // Email
        if (validation?.validations?.email && !validateEmail(value)) {
          valid = false;
          newErrors[key] = "Must be a valid email";
        }

        // Required
        if (
          (validation?.required ||
            validation?.validations?.required ||
            validation?.required?.value) &&
          valueIsEmpty(value)
        ) {
          valid = false;
          newErrors[key] =
            validation?.required?.message || "This field is required";
        }
      });

      if (!valid) {
        setErrors(newErrors);
        return false;
      }

      setErrors({});
      return true;
    },
    [form, hasBeenInit, normalizedOptions.validations]
  );

  return {
    form,
    register,
    updateForm,
    setFormInit,
    isValid,
    errors,
    clearForm,
    isFormValid,
    hasBeenInit,
  };
};

export default useForm;

const validateEmail = (email: string) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

/**
 * Creates a FormData object containing document/file assets and associated metadata
 * @param {Array} assets - Array of asset objects containing file data (uri, mimeType, name)
 * @param {Object} data - Additional metadata to attach to the form data (type, refType, id, etc)
 * @returns {FormData|null} FormData object with assets and metadata, or null if no assets
 */
export const getDocumentFormData = async (assets: any, data: any) => {
  // Return early if no assets provided
  if (!assets || assets.length === 0) return null;

  const formData = new FormData();

  // Validate FormData creation
  if (!formData) {
    console.error("Failed to create FormData");
    return null;
  }

  // Dynamically append any data fields that exist
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, String(value));
    }
  });

  // Process each asset and append to FormData
  assets.forEach((asset: any, index: number) => {
    // Extract file extension from URI or MIME type
    const extensionFromUri = asset.uri?.split(".").pop() || "";
    const extensionFromMimeType = asset.mimeType
      ? asset.mimeType.split("/").pop()
      : "";
    const extension = extensionFromUri || extensionFromMimeType || "";

    // Generate filename using metadata or construct default name
    const fileName =
      asset.name ||
      `${data.type}-${data.refType}-${data.id}-${index + 1}.${extension}`;

    // Create file object with required properties
    const fileObject = {
      uri: asset.uri,
      type: asset.mimeType || `image/${extension}`,
      name: fileName,
    };
    // Convert file object to Blob before appending to FormData
    const fileBlob = new Blob([fileObject.uri], { type: fileObject.type });
    formData.append("docs", fileBlob, fileObject.name);
  });

  return formData;
};
